import { Dispatch, FC, SetStateAction } from "react";
import { useMutation, useQueryClient } from "react-query";

import { AnalyticsBrowser } from "@segment/analytics-next";
import { endRoundingSession, postRoundingSession } from "../../api/endpoints";
import { ActionTrack } from "../../types/Analytics";
import RoundingSession from "../../types/RoundingSession";
import moment from "moment";
import { Box, Button, Colors } from "@analyticsMD/quil";
import { deepPurple } from "@mui/material/colors";

const sxStyles = {
  roundingWrapper: {
    display: "flex",
    justifyContent: "end",
  },
  button: {
    height: "2.625rem",
    width: "8.6875rem",
    textTransform: "none",
    paddingLeft: "1rem",
    paddingRight: "1rem",
  },
  startButton: {
    backgroundColor: deepPurple[600],
    color: Colors.white,
    "&:hover": {
      backgroundColor: deepPurple[600],
    },
  },
  endButton: {
    backgroundColor: Colors.white,
    color: deepPurple[600],
    border: `1px solid ${deepPurple[600]}`,
    "&:hover": {
      backgroundColor: Colors.white,
    },
  },
};

interface RoundingButtonProps {
  activeSession: RoundingSession;
  setShowRoundingToast: Dispatch<SetStateAction<boolean>>;
  setShowErrorToast: Dispatch<SetStateAction<boolean>>;
  handleDrawerOpen: () => void;
  handleDrawerClose: () => void;
  queryKey: any[];
  analytics?: AnalyticsBrowser;
}

const RoundingButtonStandalone: FC<RoundingButtonProps> = ({
  activeSession,
  setShowRoundingToast,
  setShowErrorToast,
  handleDrawerOpen,
  handleDrawerClose,
  queryKey,
  analytics,
}: RoundingButtonProps) => {
  const queryClient = useQueryClient();

  const endRoundingSessionMutation = useMutation(endRoundingSession, {
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["roundingsession"] });
      setShowRoundingToast(true);
      handleDrawerClose();
    },
    onError: () => {
      setShowErrorToast(true);
    },
  });

  const postRoundingSessionMutation = useMutation(postRoundingSession, {
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["roundingsession"] });
      queryClient.invalidateQueries({ queryKey: queryKey });
      analytics?.track(ActionTrack.ROUNDING_SESSION_STARTED, {
        session_id: data.id,
        emr_user_id: data.emr_user_id,
      });
      handleDrawerOpen();
    },
    onError: () => {
      setShowErrorToast(true);
    },
  });

  const handleStartRounds = () => {
    postRoundingSessionMutation.mutate();
  };

  const handleEndRounds = () => {
    const payload = {
      id: activeSession.id,
      end_time: moment().toISOString(true),
    };
    endRoundingSessionMutation.mutate(payload);

    analytics?.track(ActionTrack.ROUNDING_SESSION_ENDED, {
      session_id: activeSession.id,
      emr_user_id: activeSession.emr_user_id,
      end_time: payload.end_time,
    });
  };

  return (
    <Box sx={sxStyles.roundingWrapper}>
      {!activeSession && (
        <Button
          sx={{ ...sxStyles.button, ...sxStyles.startButton }}
          onClick={handleStartRounds}
        >
          Start rounds
        </Button>
      )}
      {activeSession && (
        <Box>
          <Button
            sx={{ ...sxStyles.button, ...sxStyles.endButton }}
            variant="outlined"
            onClick={handleEndRounds}
          >
            End rounds
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default RoundingButtonStandalone;
